// Per-channel / per-DM composer drafts.
//
// v1 (cyborg7-core MessageComposer.tsx) lifts unsent text + pending
// attachments into the parent, which persists them per chat (initialText /
// onTextChange / initialAttachments / onAttachmentsChange). The rewrite keeps
// that state here so switching channel/DM/thread preserves what you were
// typing and restores it when you return; a successful send clears the draft.
//
// Persistence model (three layers):
//   - In-memory `$state` is the reactive source of truth for live binding.
//   - localStorage mirrors the TEXT of each draft (key `cyborg7:draft:<ws>:<scope>`, sealed with its viewer)
//     so it survives a full page reload INSTANTLY — no round-trip needed. On first
//     access the store lazily hydrates the persisted text back in.
//   - The server (#610) holds the TEXT per (user, scope) so a draft follows the
//     user ACROSS DEVICES. Writes are debounced (one PG write per pause, not per
//     keystroke); on workspace load we fetch the server's drafts and reconcile
//     them against the local cache (newest `updatedAt` wins). A `draft_changed`
//     broadcast from the user's OTHER device live-applies here too.
//   - Pending attachments hold live `File` objects, so they are deliberately NOT
//     serialized to either layer — they can't cross a reload or a device anyway.
//     Only text persists/syncs.
//
// BACK-COMPAT: the server layer is purely additive. If the sync transport is
// never wired (e.g. a consumer that doesn't call `bindSync`), or the user is
// offline, the store degrades to the exact pre-#610 localStorage behavior — no
// data loss, existing drafts keep working.

import { authState, workspaceState } from "./core/state.svelte.js";
import { openOwned, sealOwned } from "./cache/local-owner.js";
import { beginsAsCommand } from "./plugins/agents/command-text.js";
import { clearConversationUploads } from "./composer/attachment-draft-registry.js";
import type { PendingFile } from "./components/composer/ComposerAttachments.svelte";
import { reportKpi } from "./report-failure.js";
import {
  isErasedScope,
  reconcileDrafts,
  type ReconcileLocalEntry,
  type ServerDraft,
  type ServerDraftTombstone,
} from "./draft-reconcile.js";

export {
  reconcileDrafts,
  type ReconcileLocalEntry,
  type ReconcileResult,
  type ServerDraft,
  type ServerDraftTombstone,
} from "./draft-reconcile.js";

export interface Draft {
  text: string;
  files: PendingFile[];
}

// Injected via `bindSync` so this leaf module never imports the client; unbound = local only.
export interface DraftSyncTransport {
  draftSet(workspaceId: string, scope: string, text: string, updatedAt: number): void;
  draftClear(workspaceId: string, scope: string, updatedAt: number): void;
}

const SERVER_DEBOUNCE_MS = 800;

// localStorage key for a draft's persisted text. Scoped by workspace so each
// workspace restores its own drafts (mirrors lastChannelKey in app.svelte.ts).
// `scopeId` is the opaque draft key (channel:<id>, dm:<peerId>, thread:<id>).
function storageKey(scopeId: string): string {
  const wsId = workspaceState.current?.id ?? "_";
  return `cyborg7:draft:${wsId}:${scopeId}`;
}

// Parallel key holding the epoch-ms time the local draft text was last written.
// Used as the reconcile tiebreaker so an offline edit can out-rank a stale server
// draft on the next workspace load.
function tsKey(scopeId: string): string {
  const wsId = workspaceState.current?.id ?? "_";
  return `cyborg7:draft-ts:${wsId}:${scopeId}`;
}

function readPersisted(scopeId: string): string | null {
  try {
    // Another person's draft, or a legacy plain one, is a miss (CYBO-1846 S1).
    return openOwned(authState.user?.email, localStorage.getItem(storageKey(scopeId))) ?? null; // null: a miss, this function's contract
  } catch {
    return null; // private mode / storage disabled
  }
}

function readPersistedTs(scopeId: string): number {
  try {
    const raw = localStorage.getItem(tsKey(scopeId));
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writePersisted(scopeId: string, text: string, updatedAt: number): void {
  try {
    const sealed = sealOwned(authState.user?.email, text);
    if (sealed === undefined) return; // signed out: whose text this is cannot be proven
    localStorage.setItem(storageKey(scopeId), sealed);
    localStorage.setItem(tsKey(scopeId), String(updatedAt));
  } catch {
    // intentional: best-effort draft persistence; no-op when storage is disabled.
  }
}

// A clear keeps its time (no text): an older copy can never win a reconcile against it.
function writeTombstone(scopeId: string, clearedAt: number): void {
  try {
    localStorage.removeItem(storageKey(scopeId));
    localStorage.setItem(tsKey(scopeId), String(clearedAt));
  } catch {
    // intentional: best-effort draft cleanup; no-op when storage is disabled.
  }
}

const sameFiles = (a: PendingFile[], b: PendingFile[]): boolean =>
  a.length === b.length && a.every((f, i) => f === b[i]);

// In-memory drafts, plus the cached text or clear time of each scope the server answered for.
function localCopies(
  drafts: Record<string, Draft>,
  serverScopes: Array<{ scope: string }>,
): ReconcileLocalEntry[] {
  const local = new Map<string, ReconcileLocalEntry>();
  for (const [scope, d] of Object.entries(drafts)) {
    if (d.text.trim() !== "") local.set(scope, { scope, text: d.text, updatedAt: readPersistedTs(scope) || 0 }); // prettier-ignore
  }
  for (const { scope } of serverScopes) {
    if (local.has(scope)) continue;
    const text = readPersisted(scope) ?? "";
    const updatedAt = readPersistedTs(scope);
    if (text !== "" || updatedAt > 0) local.set(scope, { scope, text, updatedAt });
  }
  return [...local.values()];
}

class DraftsState {
  // Keyed by an opaque draft key (channel:<id>, dm:<peerId>, thread:<rootId>).
  private drafts = $state<Record<string, Draft>>({});

  // Injected WS sync transport (server-side draft sync, #610). Unset until
  // app-state calls bindSync; while unset every server write is a no-op and the
  // store behaves exactly as the pre-#610 localStorage-only version.
  private sync: DraftSyncTransport | null = null;

  // Pending debounced server writes, keyed by scope. We coalesce a burst of
  // keystrokes into one PG upsert after the user pauses (SERVER_DEBOUNCE_MS).
  private flushTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // Wire the server sync transport once (from app state). Idempotent.
  // Bumped when text is placed into a composer from outside it: open composers re-read their draft.
  placed = $state(0);

  // Adds text to a conversation's draft (after whatever is already there). Never sends.
  place(key: string, text: string): void {
    const now = this.get(key);
    const mine = now?.text ?? "";
    // a half-typed "/command" stays last: placed text must never become its arguments
    const joined = beginsAsCommand(mine) ? `${text}\n\n${mine}` : `${mine}\n\n${text}`;
    this.save(key, { text: mine.trim() ? joined : text, files: now?.files ?? [] });
    this.placed++;
  }

  // Replaces a conversation's draft text (a held prompt taken into the box to edit, then the draft back).
  replace(key: string, text: string): void {
    this.save(key, { text, files: this.get(key)?.files ?? [] });
    this.placed++;
  }

  // Puts text in my composer for one agent: its DM (`cybo:<id>`) and each of its sessions
  // (`agent:<id>`), whichever the agent page shows. Never sends. Already there: left as is,
  // so a second click doesn't type it twice.
  placeForAgent(cyboId: string, text: string): void {
    const keys = new Set([`cybo:${cyboId}`]);
    for (const a of workspaceState.agents ?? []) {
      if (a.cyboId === cyboId) keys.add(`agent:${a.agentId}`);
    }
    for (const key of keys) {
      if (!this.get(key)?.text.includes(text.trim())) this.place(key, text);
    }
  }

  bindSync(transport: DraftSyncTransport): void {
    this.sync = transport;
  }

  get(key: string): Draft | undefined {
    // In-memory wins. On a miss, fall back to the persisted text from localStorage
    // so a reload restores it — returned as a FRESH Draft WITHOUT mutating reactive
    // state. get() must stay pure (no `this.drafts = …`) because it's also called
    // from inside a $derived (the sidebar's "Unsent draft" badge); mutating $state
    // there throws state_unsafe_mutation and crash-loops the app. The in-memory map
    // is filled by save() on the first edit; reads are cheap (conversation open).
    const inMem = this.drafts[key];
    if (inMem) return inMem;
    const persisted = readPersisted(key);
    return persisted != null && persisted !== "" ? { text: persisted, files: [] } : undefined;
  }

  // Persist the current composer contents for `key`. Empty drafts (no text,
  // no files) are removed so stale keys don't linger.
  save(key: string, draft: Draft): void {
    if (draft.text.trim() === "" && draft.files.length === 0) {
      this.clear(key);
      return;
    }
    // Re-saving what a composer just loaded is not an edit: no new time, no server write.
    const held = this.get(key);
    if (held && held.text === draft.text && sameFiles(held.files, draft.files)) return;
    const now = Date.now();
    // $state objects are deeply proxied in Svelte 5: assigning a single key
    // mutates in place and triggers only that key's subscribers (more surgical
    // than reassigning the whole object).
    this.drafts[key] = { text: draft.text, files: draft.files };
    // Mirror only the text to localStorage (live File objects can't serialize).
    // Instant cache: written synchronously on every keystroke.
    writePersisted(key, draft.text, now);
    // Server sync: debounced so a burst of typing is one PG upsert, not one per
    // keystroke. The TEXT is the only thing synced (attachments can't cross).
    this.scheduleServerSet(key, draft.text);
  }

  clear(key: string): void {
    // Did this draft exist anywhere (memory / persisted cache / a pending flush)?
    // clear() runs on every conversation switch and blur, so only touch the server
    // when there was actually something to clear — otherwise we'd emit a useless
    // draft_clear frame (and broadcast) on every blur over an empty composer.
    const pending = this.flushTimers.get(key);
    const existed = key in this.drafts || readPersisted(key) != null || pending !== undefined;

    if (pending) {
      clearTimeout(pending);
      this.flushTimers.delete(key);
    }
    const wsId = workspaceState.current?.id;
    if (existed) {
      const now = Date.now();
      writeTombstone(key, now);
      if (this.sync && wsId) this.sync.draftClear(wsId, key, now);
    }
    if (key in this.drafts) delete this.drafts[key];
  }

  // ─── Server-side draft sync (#610) ───────────────────────────────

  private scheduleServerSet(key: string, text: string): void {
    const wsId = workspaceState.current?.id;
    if (!this.sync || !wsId) return; // unbound / no active workspace → localStorage only
    const sync = this.sync;
    const existing = this.flushTimers.get(key);
    if (existing) clearTimeout(existing);
    this.flushTimers.set(
      key,
      setTimeout(() => {
        this.flushTimers.delete(key);
        // Re-read the active workspace at flush time; a switch mid-debounce must
        // not write the new draft under the old workspace's id.
        const liveWs = workspaceState.current?.id;
        if (liveWs === wsId) sync.draftSet(wsId, key, text, Date.now());
      }, SERVER_DEBOUNCE_MS),
    );
  }

  // text null = cleared on the other device; older than this device's last write = stale.
  applyRemoteChange(scope: string, text: string | null, updatedAt: number): void {
    if (isErasedScope(scope) || updatedAt < readPersistedTs(scope)) return;
    if (text === null || text === "") {
      writeTombstone(scope, updatedAt);
      if (scope in this.drafts) delete this.drafts[scope];
      return;
    }
    writePersisted(scope, text, updatedAt);
    const existing = this.drafts[scope];
    this.drafts[scope] = { text, files: existing?.files ?? [] };
  }

  // Workspace load: apply reconcileDrafts' winners, drop stale copies, re-send newer local writes.
  seedFromServer(serverDrafts: ServerDraft[], tombstones: ServerDraftTombstone[] = []): void {
    const wsId = workspaceState.current?.id;
    const local = localCopies(this.drafts, [...serverDrafts, ...tombstones]);
    const { apply, pushBack, drop, clearBack } = reconcileDrafts(serverDrafts, local, tombstones);

    const winnerTs = new Map(serverDrafts.map((s) => [s.scope, s.updatedAt]));
    for (const l of pushBack) if (l.updatedAt > 0) winnerTs.set(l.scope, l.updatedAt);
    for (const [scope, text] of apply) {
      this.drafts[scope] = { text, files: this.drafts[scope]?.files ?? [] };
      writePersisted(scope, text, winnerTs.get(scope) ?? (readPersistedTs(scope) || Date.now()));
    }
    for (const [scope, clearedAt] of drop) {
      writeTombstone(scope, clearedAt);
      if (scope in this.drafts) delete this.drafts[scope];
    }
    if (!wsId) return;
    if (drop.size > 0) reportKpi("drafts.stale_dropped", { count: drop.size, workspaceId: wsId });
    for (const l of pushBack) this.sync?.draftSet(wsId, l.scope, l.text, l.updatedAt || Date.now());
    for (const l of clearBack) this.sync?.draftClear(wsId, l.scope, l.updatedAt);
  }

  // Reconnect bootstrap; drafts are keyed by the active workspace, so only its own applies.
  seedFromBootstrap(
    workspaceId: string,
    boot: { drafts?: ServerDraft[]; draftTombstones?: ServerDraftTombstone[] },
  ): void {
    if (workspaceState.current?.id !== workspaceId || !boot.drafts) return;
    this.seedFromServer(boot.drafts, boot.draftTombstones);
  }

  // Drop all in-memory drafts (logout / account switch). localStorage is left
  // intact (it's workspace-scoped and per-origin) so a re-login restores cache;
  // server truth re-seeds on the next workspace load.
  clearAllLocal(): void {
    clearConversationUploads();
    for (const t of this.flushTimers.values()) clearTimeout(t);
    this.flushTimers.clear();
    this.drafts = {};
  }
}

export const draftsState = new DraftsState();
